import { apiAuthTwoFactorCreate, apiAuthTwoFactorTurnOn } from "@lib/api/auth";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseQueryProps } from "@lib/types/Api";
import { Manager } from "@lib/types/Manager";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Image, Modal, Spin, Typography } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  query: UseQueryProps<Manager>;
};

export default function TwoFactorModal(props: Props) {
  const { openModal, toggleModal, query } = props;
  const { messages } = useIntl();
  const userData = query?.data?.data;

  const turnOnQuery = useMutation({
    mutationFn: apiAuthTwoFactorTurnOn,
    onSuccess() {
      query.refetch();
    },
  });

  useEffect(() => {
    if (openModal && !userData?.isTwoFactorAuthEnabled) {
      turnOnQuery.mutate();
    }
  }, [userData, openModal]);

  const { data, isLoading, isFetching } = useQuery({
    queryFn: apiAuthTwoFactorCreate,
    queryKey: ["qr-generate", userData?.id],
    enabled: openModal && userData?.isTwoFactorAuthEnabled,
  });
  const loading = turnOnQuery.isPending || isLoading || isFetching;

  const image = useMemo(() => {
    if (!data) return null;
    const blob = new Blob([data.data], { type: "image/png" });
    return URL.createObjectURL(blob);
  }, [data]);

  const handleClose = useCallback(() => {
    toggleModal(false);
  }, [toggleModal]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={"common.createTitle"}
          values={{ title: messages["common.twoFactor"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
    >
      <Typography.Title style={{ fontSize: "20px" }}>
        {messages["userProfile.twoFactor.modalTitle"] as string}
      </Typography.Title>
      <Typography.Paragraph style={{ fontSize: "12px" }}>
        {messages["userProfile.twoFactor.modalDesc"] as string}
      </Typography.Paragraph>

      <StyledFlex
        style={{
          width: "100%",
          minHeight: "350px",
          justifyContent: "center",
          marginBlock: "50px",
        }}
      >
        {loading ? (
          <Spin />
        ) : (
          <Image
            src={image}
            style={{ width: "350px", height: "auto" }}
            preview={false}
          />
        )}
      </StyledFlex>
    </Modal>
  );
}
